<div class="flex flex-col h-screen bg-slate-100 overflow-hidden">
  <header class="bg-white shadow-sm flex-shrink-0 z-10">
    <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-slate-900">SuperYes Onboarding</h1>
        <div class="flex items-center space-x-4">
          @if(reviewer(); as rev) {
            <a routerLink="/admin" class="text-sm font-medium text-indigo-600 hover:underline">Manage Users</a>
            <div class="text-right">
              <p class="text-sm font-medium text-slate-800">{{ rev.fullName }}</p>
              <button (click)="logout()" class="text-xs text-indigo-600 hover:underline">Logout</button>
            </div>
          } @else {
             <button (click)="logout()" class="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors">Logout</button>
          }
        </div>
      </div>
    </div>
  </header>

  <!-- Search and Sort Controls -->
  <div class="flex-shrink-0 bg-slate-50 border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <div class="relative w-full max-w-sm">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
          </svg>
        </div>
        <input 
          type="text"
          [value]="searchTerm()"
          #searchInput
          (input)="onSearch(searchInput.value)"
          placeholder="Search by fleet, contact, email..."
          class="block w-full rounded-md border-0 bg-white py-2 pl-10 text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
      </div>

      <div class="flex items-center space-x-2">
        <label for="sort-by" class="text-sm font-medium text-slate-600">Sort by:</label>
        <select 
          id="sort-by"
          [value]="sortOption()"
          #sortSelect
          (change)="onSort(sortSelect.value)"
          class="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-slate-900 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
          <option value="updated_at_desc">Most Recently Updated</option>
          <option value="created_at_desc">Newest First</option>
          <option value="created_at_asc">Oldest First</option>
        </select>
      </div>
    </div>
  </div>

  <main class="flex-grow min-h-0">
    @if (loading()) {
      <div class="flex h-full space-x-4 p-4 overflow-x-auto animate-pulse">
        <!-- Skeleton Column -->
        @for (_ of [1, 2, 3, 4, 5]; track $index) {
          <div class="w-80 flex-shrink-0 bg-slate-200 rounded-lg shadow-inner flex flex-col">
            <!-- Skeleton Header -->
            <div class="p-2 z-10 border-b border-slate-300">
              <div class="flex items-center justify-between">
                <div class="h-4 bg-slate-300 rounded w-24"></div>
                <div class="h-5 w-8 bg-slate-300 rounded-full"></div>
              </div>
            </div>
            <!-- Skeleton Cards -->
            <div class="p-2 space-y-2">
              <div class="bg-slate-300/50 rounded-md p-3 h-24 w-full">
                <div class="h-3 bg-slate-300 rounded w-3/4"></div>
                <div class="h-2 bg-slate-300 rounded w-1/2 mt-2"></div>
                <div class="h-2 bg-slate-300 rounded w-1/3 mt-3"></div>
              </div>
              <div class="bg-slate-300/50 rounded-md p-3 h-24 w-full">
                <div class="h-3 bg-slate-300 rounded w-3/4"></div>
                <div class="h-2 bg-slate-300 rounded w-1/2 mt-2"></div>
                <div class="h-2 bg-slate-300 rounded w-1/3 mt-3"></div>
              </div>
              <div class="bg-slate-300/50 rounded-md p-3 h-24 w-full">
                <div class="h-3 bg-slate-300 rounded w-3/4"></div>
                <div class="h-2 bg-slate-300 rounded w-1/2 mt-2"></div>
                <div class="h-2 bg-slate-300 rounded w-1/3 mt-3"></div>
              </div>
            </div>
          </div>
        }
      </div>
    } @else if (error()) {
      <div class="p-8">
        <div class="bg-red-50 border-l-4 border-red-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">{{ error() }}</p>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <div class="flex h-full space-x-4 p-4 overflow-x-auto">
        <!-- Draft Column -->
        <div [class]="columnState()['draft'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['draft']) {
                <h3 class="text-sm font-semibold text-slate-700">Draft</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ draftApps().length }}</span>
              <button (click)="toggleColumn('draft')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['draft']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['draft']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of draftApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150 opacity-80">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  @if(app.current_step) {
                    <p class="text-xs text-slate-600 mt-1 capitalize"><span class="font-medium">Progress:</span> {{ app.current_step.replace('_', ' ') }}</p>
                  }
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.created_at) }}</span>
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-0.5 font-semibold rounded-full bg-slate-100 text-slate-800 text-[10px]">DRAFT</span>
                      <button (click)="promptDelete($event, app)" title="Delete application" class="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No draft applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('draft')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Draft</h3>
            </div>
          }
        </div>
        
        <!-- Submitted Column -->
        <div [class]="columnState()['submitted'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['submitted']) {
                <h3 class="text-sm font-semibold text-slate-700">Submitted</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ submittedApps().length }}</span>
              <button (click)="toggleColumn('submitted')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                 <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['submitted']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['submitted']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of submittedApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-0.5 font-semibold rounded-full bg-blue-100 text-blue-800 text-[10px]">NEW</span>
                      <button (click)="promptDelete($event, app)" title="Delete application" class="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No new applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('submitted')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Submitted</h3>
            </div>
          }
        </div>

        <!-- In Review Column -->
        <div [class]="columnState()['in_review'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['in_review']) {
                <h3 class="text-sm font-semibold text-slate-700">In Review</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ inReviewApps().length }}</span>
              <button (click)="toggleColumn('in_review')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['in_review']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['in_review']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of inReviewApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span class="px-2 py-0.5 font-semibold rounded-full bg-yellow-100 text-yellow-800 text-[10px]">REVIEW</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No applications in review.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('in_review')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">In Review</h3>
            </div>
          }
        </div>

        <!-- Approved Column -->
        <div [class]="columnState()['approved'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['approved']) {
                <h3 class="text-sm font-semibold text-slate-700">Approved</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ approvedApps().length }}</span>
              <button (click)="toggleColumn('approved')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['approved']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['approved']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of approvedApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span [class]="'px-2 py-0.5 font-semibold rounded-full text-[10px] ' + getBuildStatusClass(app.latestWebhook)">{{ getBuildStatus(app.latestWebhook) }}</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No approved applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('approved')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Approved</h3>
            </div>
          }
        </div>

        <!-- Ready for Go Live Column -->
        <div [class]="columnState()['ready_for_go_live'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['ready_for_go_live']) {
                <h3 class="text-sm font-semibold text-slate-700">Ready for Go Live</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ readyForGoLiveApps().length }}</span>
              <button (click)="toggleColumn('ready_for_go_live')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['ready_for_go_live']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['ready_for_go_live']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of readyForGoLiveApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span class="px-2 py-0.5 font-semibold rounded-full bg-cyan-100 text-cyan-800 text-[10px]">READY</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No applications ready for go live.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('ready_for_go_live')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Ready for Go Live</h3>
            </div>
          }
        </div>

        <!-- Live Column -->
        <div [class]="columnState()['live'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['live']) {
                <h3 class="text-sm font-semibold text-slate-700">Live</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ liveApps().length }}</span>
              <button (click)="toggleColumn('live')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['live']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['live']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of liveApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span class="px-2 py-0.5 font-semibold rounded-full bg-lime-100 text-lime-800 text-[10px]">LIVE</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No live applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('live')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Live</h3>
            </div>
          }
        </div>
        
        <!-- Suspended Column -->
        <div [class]="columnState()['suspended'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['suspended']) {
                <h3 class="text-sm font-semibold text-slate-700">Suspended</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ suspendedApps().length }}</span>
              <button (click)="toggleColumn('suspended')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['suspended']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['suspended']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of suspendedApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span class="px-2 py-0.5 font-semibold rounded-full bg-orange-100 text-orange-800 text-[10px]">SUSPENDED</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No suspended applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('suspended')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Suspended</h3>
            </div>
          }
        </div>

        <!-- Rejected Column -->
        <div [class]="columnState()['rejected'] ? 'w-16' : 'w-80'" class="flex-shrink-0 bg-slate-200 rounded-lg shadow-inner transition-all duration-300 ease-in-out flex flex-col">
          <div class="sticky top-0 bg-slate-200 rounded-t-lg p-2 z-10 border-b border-slate-300">
            <div class="flex items-center justify-between">
              @if(!columnState()['rejected']) {
                <h3 class="text-sm font-semibold text-slate-700">Rejected</h3>
              }
              <div class="flex-grow"></div>
              <span class="px-2 py-0.5 text-xs font-medium bg-slate-300 text-slate-600 rounded-full">{{ rejectedApps().length }}</span>
              <button (click)="toggleColumn('rejected')" class="ml-2 p-1 rounded-full hover:bg-slate-300 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-600 transition-transform" [class.rotate-180]="columnState()['rejected']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
            </div>
          </div>
          @if(!columnState()['rejected']) {
            <div class="p-2 space-y-2 overflow-y-auto h-full">
              @for (app of rejectedApps(); track app.id) {
                <div (click)="viewApplication(app.id)" class="bg-white rounded-md shadow p-3 cursor-pointer border-2 border-transparent hover:border-indigo-500 hover:shadow-lg transition-all duration-150 opacity-70">
                  <p class="font-semibold text-sm text-slate-800">{{ app.mergedData?.['fleet_name'] || 'Unnamed Fleet' }}</p>
                  <p class="text-xs text-slate-500 mt-1">{{ app.mergedData?.['contact_name'] || 'No Contact' }}</p>
                  <div class="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <span>{{ formatDate(app.submitted_at || app.created_at) }}</span>
                    <span class="px-2 py-0.5 font-semibold rounded-full bg-red-100 text-red-800 text-[10px]">REJECTED</span>
                  </div>
                </div>
              } @empty {
                <div class="p-4 text-center text-sm text-slate-500">No rejected applications.</div>
              }
            </div>
          } @else {
            <div (click)="toggleColumn('rejected')" class="flex-grow flex items-center justify-center cursor-pointer">
              <h3 class="text-sm font-semibold text-slate-700 transform -rotate-90 whitespace-nowrap">Rejected</h3>
            </div>
          }
        </div>
      </div>
    }
  </main>
</div>

@if (appToDelete(); as target) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" (click)="cancelDelete()">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6" (click)="$event.stopPropagation()">
      <div class="flex items-start gap-4">
        <div class="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
          <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
          </svg>
        </div>
        <div class="flex-1">
          <h3 class="text-base font-semibold text-slate-900">Delete Application</h3>
          <p class="mt-1 text-sm text-slate-600">
            Are you sure you want to permanently delete <strong>{{ target.name }}</strong>? All associated data and uploaded documents will be removed. This cannot be undone.
          </p>
          @if (deleteError()) {
            <p class="mt-2 text-sm text-red-600">{{ deleteError() }}</p>
          }
        </div>
      </div>
      <div class="mt-5 flex justify-end gap-3">
        <button
          type="button"
          (click)="cancelDelete()"
          [disabled]="isDeleting()"
          class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="confirmDelete()"
          [disabled]="isDeleting()"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          @if (isDeleting()) {
            <svg class="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Deleting...
          } @else {
            Delete
          }
        </button>
      </div>
    </div>
  </div>
}